import { useMemo } from 'react'

import {
  matchFilterKey, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TrackingLinkModel } from '@models/types'
import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['trackingLinks']

type TrackingLinkFilters = {
  recipient_id?: number,
  recipient_type?: string,
}

type UseTrackingLinksOptions = {
  filters?: TrackingLinkFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useTrackingLinks(options: UseTrackingLinksOptions) {
  const { filters = {}, requestOptions } = options
  const {
    recipient_id: filterRecipientId,
    recipient_type: filterRecipientType,
  } = filters

  const {
    updatedEntities: { trackingLinks: trackingLinksUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { trackingLinks } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredTrackingLinks = useMemo(() => {
    const filtered = Object.values(trackingLinks).filter((trackingLink: TrackingLinkModel) => {
      const { recipient_id, recipient_type } = trackingLink

      const matchRecipientId = matchFilterKey(recipient_id, filterRecipientId)
      const matchRecipientType = matchFilterKey(recipient_type, filterRecipientType)

      return matchRecipientType && matchRecipientId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [trackingLinksUpdatedAt, JSON.stringify(filters)])

  const filteredTrackingLinksCount = filteredTrackingLinks.length
  const hasFilteredTrackingLinks = !!filteredTrackingLinksCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTrackingLinksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingTrackingLinks } = useReduxAction(
    'trackingLinks',
    'loadTrackingLinks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTrackingLinks,
    filteredTrackingLinksCount,
    hasFilteredTrackingLinks,
    loading: loadingTrackingLinks,
  }
}

export default useTrackingLinks
